import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthPage, type AuthRuntimeConfig, type AuthScreen } from '@ai-factory/auth-sdk';
import { BillingPage, type BillingRuntimeConfig, type BillingScreen } from '@ai-factory/billing-ui';
import { ApplicationFailureState } from '@ai-factory/ui-core';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom';
import { applyDocumentLocale, applyHreflangLinks, browserLocaleContext, createAppTranslator } from '@ai-factory/i18n';
import { application } from './application';
import { authBrand, brand } from './brand';

// The console data plane is meaningful only after an administrator has
// authenticated. Keep it outside public authentication and billing bundles so
// operators do not download every management surface before the route needs it.
const ConsoleGuard = lazy(() => import('./console').then(({ ConsoleGuard: page }) => ({ default: page })));
const ConsoleVisualPreview = lazy(() => import('./console').then(({ ConsoleVisualPreview: page }) => ({ default: page })));
const DashboardVisualPreview = lazy(() => import('./console').then(({ DashboardVisualPreview: page }) => ({ default: page })));
const DashboardPage = lazy(() => import('./console').then(({ DashboardPage: page }) => ({ default: page })));
const CatalogPage = lazy(() => import('./console').then(({ CatalogPage: page }) => ({ default: page })));
const CreditWalletsPage = lazy(() => import('./console').then(({ CreditWalletsPage: page }) => ({ default: page })));
const EntitlementsPage = lazy(() => import('./console').then(({ EntitlementsPage: page }) => ({ default: page })));
const FeatureFlagsPage = lazy(() => import('./console').then(({ FeatureFlagsPage: page }) => ({ default: page })));
const ObservabilityPage = lazy(() => import('./console').then(({ ObservabilityPage: page }) => ({ default: page })));
const QualityEvaluationsPage = lazy(() => import('./console').then(({ QualityEvaluationsPage: page }) => ({ default: page })));
const SettingsPage = lazy(() => import('./console').then(({ SettingsPage: page }) => ({ default: page })));
const SystemHealthPage = lazy(() => import('./console').then(({ SystemHealthPage: page }) => ({ default: page })));
const ResourcePageRoute = lazy(() => import('./console').then(({ ResourcePage, resourceConfig }) => ({
  default: ({ resourceKey }: { resourceKey: string }) => <ResourcePage config={resourceConfig(resourceKey)} />,
})));
const DetailPageRoute = lazy(() => import('./console').then(({ DetailPage, resourceConfig }) => ({
  default: ({ resourceKey }: { resourceKey: string }) => <DetailPage config={resourceConfig(resourceKey)} />,
})));
const PromptVersionDetailPageRoute = lazy(() => import('./console').then(({ DetailPage }) => ({
  default: () => <DetailPage config={{ resource: 'prompt_versions', title: 'Prompt version', singular: 'Prompt version', description: '', preferredColumns: [] }} />,
})));

const localeContext = browserLocaleContext();
const routeText = createAppTranslator('state', localeContext.locale);
const supportHref = import.meta.env.VITE_SUPPORT_URL?.trim();
applyDocumentLocale(localeContext);
applyHreflangLinks(window.location.pathname, window.location.origin);

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabasePublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
if (!supabaseUrl || !supabasePublishableKey) throw new Error('SUPABASE_PUBLIC_CONFIG_MISSING');

const config: AuthRuntimeConfig = {
  applicationCode: application.code,
  audience: 'internal',
  supabaseUrl,
  supabasePublishableKey,
  siteUrl: import.meta.env.VITE_SITE_URL || window.location.origin,
};

function authenticatedDestination() {
  // Preserve a same-origin destination for protected deep links. External
  // URLs and protocol-relative paths fall back to the dashboard so login can
  // never become an open redirect.
  const next = new URLSearchParams(window.location.search).get('next');
  return next && /^\/(?!\/)/.test(next) ? next : '/';
}

const auth = (screen: AuthScreen) => (
  <AuthPage
    screen={screen}
    config={config}
    brand={authBrand}
    resolveSuccessPath={() => authenticatedDestination()}
  />
);
const billingConfig: BillingRuntimeConfig = {
  applicationCode: application.code,
  supabaseUrl,
  supabasePublishableKey,
  paddleClientToken: import.meta.env.VITE_PADDLE_CLIENT_TOKEN,
  paddleEnvironment:
    import.meta.env.VITE_PADDLE_ENVIRONMENT === 'production' ? 'production' : 'sandbox',
  siteUrl: import.meta.env.VITE_SITE_URL || window.location.origin,
  locale: localeContext.locale,
  timezone: localeContext.timezone,
};
const billing = (screen: BillingScreen) => (
  <BillingPage screen={screen} config={billingConfig} brand={brand} />
);
const visualPreviewRoutes = import.meta.env.DEV
  ? [{ path: '/__task022-preview', element: <ConsoleVisualPreview />, children: [{ index: true, element: <DashboardVisualPreview /> }] }]
  : [];
const routes = [
  { path: '/login', element: auth('login') },
  { path: '/signup', element: auth('signup') },
  { path: '/forgot-password', element: auth('forgot-password') },
  { path: '/verify-email', element: auth('email-verification') },
  { path: '/auth/callback', element: auth('email-verification') },
  { path: '/invitation', element: auth('invitation') },
  { path: '/suspended', element: auth('suspended') },
  { path: '/pricing', element: billing('pricing') },
  { path: '/billing', element: billing('billing') },
  { path: '/checkout/result', element: billing('checkout-result') },
  { path: '/billing/past-due', element: billing('past-due') },
  { path: '/billing/cancel', element: billing('cancel') },
  ...visualPreviewRoutes,
  {
    element: <ConsoleGuard />,
    children: [
      { path: '/', element: <DashboardPage /> },
      { path: '/applications', element: <ResourcePageRoute resourceKey="applications" /> },
      { path: '/applications/:id', element: <DetailPageRoute resourceKey="applications" /> },
      { path: '/users', element: <ResourcePageRoute resourceKey="users" /> },
      { path: '/users/:id', element: <DetailPageRoute resourceKey="users" /> },
      { path: '/organizations', element: <ResourcePageRoute resourceKey="organizations" /> },
      { path: '/organizations/:id', element: <DetailPageRoute resourceKey="organizations" /> },
      { path: '/app-accounts', element: <ResourcePageRoute resourceKey="app_accounts" /> },
      { path: '/app-accounts/:id', element: <DetailPageRoute resourceKey="app_accounts" /> },
      { path: '/subscriptions', element: <ResourcePageRoute resourceKey="subscriptions" /> },
      { path: '/subscriptions/:id', element: <DetailPageRoute resourceKey="subscriptions" /> },
      { path: '/checkout-orders', element: <ResourcePageRoute resourceKey="checkout_orders" /> },
      { path: '/webhooks', element: <ResourcePageRoute resourceKey="webhook_events" /> },
      { path: '/catalog', element: <CatalogPage /> },
      { path: '/entitlements', element: <EntitlementsPage /> },
      { path: '/credit-wallets', element: <CreditWalletsPage /> },
      { path: '/credit-ledger', element: <ResourcePageRoute resourceKey="credit_ledger" /> },
      { path: '/ai-jobs', element: <ResourcePageRoute resourceKey="ai_jobs" /> },
      { path: '/ai-jobs/:id', element: <DetailPageRoute resourceKey="ai_jobs" /> },
      { path: '/providers', element: <ResourcePageRoute resourceKey="providers" /> },
      { path: '/providers/:id', element: <DetailPageRoute resourceKey="providers" /> },
      { path: '/models', element: <ResourcePageRoute resourceKey="models" /> },
      { path: '/routes', element: <ResourcePageRoute resourceKey="routes" /> },
      { path: '/routes/:id', element: <DetailPageRoute resourceKey="routes" /> },
      { path: '/prompts', element: <ResourcePageRoute resourceKey="prompt_templates" /> },
      { path: '/prompts/:id', element: <DetailPageRoute resourceKey="prompt_templates" /> },
      { path: '/prompt-versions/:id', element: <PromptVersionDetailPageRoute /> },
      { path: '/ai-usage', element: <ResourcePageRoute resourceKey="ai_costs" /> },
      { path: '/rag-monitor', element: <ResourcePageRoute resourceKey="rag_processing" /> },
      { path: '/quality-evaluations', element: <QualityEvaluationsPage /> },
      { path: '/analytics', element: <ResourcePageRoute resourceKey="analytics" /> },
      { path: '/audit', element: <ResourcePageRoute resourceKey="audit_logs" /> },
      { path: '/sensitive-access', element: <ResourcePageRoute resourceKey="sensitive_access_logs" /> },
      { path: '/system-health', element: <SystemHealthPage /> },
      { path: '/observability', element: <ObservabilityPage /> },
      { path: '/feature-flags', element: <FeatureFlagsPage /> },
      { path: '/settings', element: <SettingsPage /> },
    ],
  },
  { path: '*', element: <Navigate to="/login" replace /> },
];
const router = createBrowserRouter([{
  element: <Outlet />,
  errorElement: (
    <ApplicationFailureState
      title={routeText('state.route_error_title')}
      description={routeText('state.route_error_body')}
      retryLabel={routeText('state.retry')}
      onRetry={() => window.location.reload()}
      {...(supportHref ? { supportHref, supportLabel: routeText('state.contact_support') } : {})}
    />
  ),
  children: routes,
}]);
const root = document.getElementById('root');
if (!root) throw new Error('APPLICATION_ROOT_MISSING');
createRoot(root).render(
  <StrictMode>
    <Suspense fallback={<main aria-busy="true" aria-live="polite"><p>Loading the Platform Console…</p></main>}>
      <RouterProvider router={router} />
    </Suspense>
  </StrictMode>,
);
